import { ReactNode, createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, useId } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';
import { MODAL_OVERLAY_CLASS, MODAL_OVERLAY_STYLE, MODAL_PANEL_CLASS, useDialog } from './Modal';
import Button from './Button';
import { TEXT } from './Typography';

/* -------------------------------- Types ----------------------------------- */
type NotificationVariant = 'success' | 'error' | 'warning' | 'info';

interface NotificationOptions {
  variant: NotificationVariant;
  title?: string;
  message: string;
  details?: string;
  duration?: number; // Auto-close after ms (0 = manual close only)
  actions?: ReactNode;
}

interface NotificationContextValue {
  show: (options: NotificationOptions) => void;
  showError: (message: string, title?: string, details?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string, details?: string) => void;
  showInfo: (message: string, title?: string) => void;
  close: () => void;
}

/* ----------------------------- Variant Config ----------------------------- */
const variantConfig = {
  success: {
    icon: CheckCircleIcon,
    iconColor: 'text-green-600',
    accentColor: 'bg-green-600',
  },
  error: {
    icon: XCircleIcon,
    iconColor: 'text-red-600',
    accentColor: 'bg-red-600',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconColor: 'text-amber-600',
    accentColor: 'bg-amber-600',
  },
  info: {
    icon: InformationCircleIcon,
    iconColor: 'text-blue-600',
    accentColor: 'bg-blue-600',
  },
};

const defaultTitles: Record<NotificationVariant, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Information',
};

/* -------------------------------- Context --------------------------------- */
const NotificationContext = createContext<NotificationContextValue | null>(null);

/* ------------------------------ Provider ---------------------------------- */
interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notification, setNotification] = useState<NotificationOptions | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((options: NotificationOptions) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setNotification(options);
    setIsVisible(true);

    // Auto-close if duration is set (success messages default to auto-close)
    const duration = options.duration ?? (options.variant === 'success' ? 4000 : 0);
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        setIsVisible(false);
      }, duration);
    }
  }, []);

  const close = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  }, []);

  const showError = useCallback((message: string, title?: string, details?: string) => {
    show({ variant: 'error', message, title, details });
  }, [show]);

  const showSuccess = useCallback((message: string, title?: string) => {
    show({ variant: 'success', message, title, duration: 4000 });
  }, [show]);

  const showWarning = useCallback((message: string, title?: string, details?: string) => {
    show({ variant: 'warning', message, title, details });
  }, [show]);

  const showInfo = useCallback((message: string, title?: string) => {
    show({ variant: 'info', message, title });
  }, [show]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({ show, showError, showSuccess, showWarning, showInfo, close }),
    [show, showError, showSuccess, showWarning, showInfo, close]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notification && isVisible && notification.variant === 'success' && (
        <SuccessToast title={notification.title} message={notification.message} onClose={close} />
      )}
      {notification && notification.variant !== 'success' && (
        <NotificationModalContent
          {...notification}
          isOpen={isVisible}
          onClose={close}
        />
      )}
    </NotificationContext.Provider>
  );
}

/* -------------------------------- Hook ------------------------------------ */
export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

/* ----------------------------- Success Toast ------------------------------ */
/**
 * Success is confirmation, not a question, so it must not cost a click. It
 * used to be the same blocking modal as an error, with an OK button, after
 * nearly every save and approve. It now sits clear of the page, blocks
 * nothing and fades on the provider's timer; clicking it dismisses early.
 * Errors and warnings stay modal because those have to be read.
 */
function SuccessToast({ title, message, onClose }: { title?: string; message: string; onClose: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      onClick={onClose}
      className="fixed top-safe-4 left-1/2 -translate-x-1/2 z-[100] w-max max-w-[calc(100vw-2rem)] sm:max-w-md flex items-start gap-3 bg-white border border-gray-200 border-l-4 border-l-green-600 shadow-md rounded-lg px-4 py-3 cursor-pointer"
    >
      <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
      <p className={`leading-snug ${TEXT.body}`}>
        {title && <span className={TEXT.emphasis}>{title} </span>}
        {message}
      </p>
    </div>
  );
}

/* ----------------------------- Modal Content ------------------------------ */
interface NotificationModalContentProps extends NotificationOptions {
  isOpen: boolean;
  onClose: () => void;
}

function NotificationModalContent({
  variant,
  title,
  message,
  details,
  actions,
  isOpen,
  onClose,
}: NotificationModalContentProps) {
  // Focus, focus trap, scroll lock and Escape, shared with Modal -- an error
  // shown over a dialog closes on its own without closing the dialog under it.
  const titleId = useId();
  const { panelRef, trapFocus } = useDialog({ isOpen, onClose });

  if (!isOpen) return null;

  const config = variantConfig[variant];
  const Icon = config.icon;
  const displayTitle = title || defaultTitles[variant];

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`z-[100] ${MODAL_OVERLAY_CLASS}`}
      onClick={handleBackdropClick}
      style={MODAL_OVERLAY_STYLE}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={trapFocus}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`${MODAL_PANEL_CLASS} relative w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
        onClick={e => e.stopPropagation()}
      >
        {/* Accent bar at top */}
        <div className={`h-1 ${config.accentColor}`} />

        {/* Close button */}
        <CloseButton
          onClick={onClose}
          position="absolute"
          label="Close notification"
        />

        {/* Content */}
        <div className="p-6">
          {/* Header with logo */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
            <img
              src="/grautomates-dark.png"
              alt="GRPS"
              className="h-8 w-auto"
            />
            <div className="h-6 w-px bg-gray-200" />
            <span className={TEXT.detailLabel}>System Notification</span>
          </div>

          {/* Icon and Title */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Icon className={`h-6 w-6 ${config.iconColor}`} aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id={titleId}
                className={TEXT.sectionTitle}
              >
                {displayTitle}
              </h3>
              <p className={`mt-1 leading-relaxed ${TEXT.bodyMuted}`}>{message}</p>
            </div>
          </div>

          {/* Details (expandable error details) */}
          {details && (
            <div className={`mt-4 p-3 rounded-md bg-gray-50 border border-gray-200 font-mono whitespace-pre-wrap break-words max-h-32 overflow-y-auto ${TEXT.body}`}>
              {details}
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            {actions}
            <Button variant="primary" size="sm" onClick={onClose} className="min-h-[44px] sm:min-h-0 touch-manipulation">
              {variant === 'error' || variant === 'warning' ? 'Dismiss' : 'OK'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Standalone Modal Component ----------------------- */
interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: NotificationVariant;
  title?: string;
  message: string;
  details?: string;
  actions?: ReactNode;
}

export default function NotificationModal(props: NotificationModalProps) {
  return <NotificationModalContent {...props} />;
}
